import { Button } from "@shared/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import type { Table } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { FormattedMessage } from "react-intl";

type TablePaginationProps<T> = {
	table: Table<T>;
	totalRows: number;
};

export function TablePagination<T>({ table, totalRows }: TablePaginationProps<T>) {
	const pageIndex = table.getState().pagination.pageIndex;
	const pageSize = table.getState().pagination.pageSize;
	const from = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
	const to = Math.min((pageIndex + 1) * pageSize, totalRows);

	return (
		<div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
			<div className="text-xs text-muted-foreground">
				<FormattedMessage
					id="table.pagination.showing"
					defaultMessage="Showing {from} - {to} of {total}"
					values={{
						from,
						to,
						total: totalRows,
					}}
				/>
			</div>
			<div className="flex items-center gap-4">
				<div className="flex items-center gap-2">
					<span className="text-xs text-muted-foreground">
						<FormattedMessage id="table.pagination.rowsPerPage" defaultMessage="Rows per page" />
					</span>
					<Select
						value={String(pageSize)}
						onValueChange={(v) => table.setPageSize(Number(v))}
					>
						<SelectTrigger className="h-8 w-[70px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{[10, 20, 30, 50].map((s) => (
								<SelectItem key={s} value={String(s)}>
									{s}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-1">
					<Button
						variant="outline"
						size="icon"
						className="h-8 w-8"
						onClick={() => table.setPageIndex(0)}
						disabled={!table.getCanPreviousPage()}
					>
						<ChevronsLeft className="h-4 w-4" />
					</Button>
					<Button
						variant="outline"
						size="icon"
						className="h-8 w-8"
						onClick={() => table.previousPage()}
						disabled={!table.getCanPreviousPage()}
					>
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<span className="px-2 text-xs text-muted-foreground">
						<FormattedMessage
							id="table.pagination.page"
							defaultMessage="Page {current} of {total}"
							values={{
								current: pageIndex + 1,
								total: table.getPageCount() || 1,
							}}
						/>
					</span>
					<Button
						variant="outline"
						size="icon"
						className="h-8 w-8"
						onClick={() => table.nextPage()}
						disabled={!table.getCanNextPage()}
					>
						<ChevronRight className="h-4 w-4" />
					</Button>
					<Button
						variant="outline"
						size="icon"
						className="h-8 w-8"
						onClick={() => table.setPageIndex(table.getPageCount() - 1)}
						disabled={!table.getCanNextPage()}
					>
						<ChevronsRight className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
}
